import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, BookOpen, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/sports-betting/money-history`;
const usd = (n) => `$${Math.abs(Number(n) || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const signed = (n) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${usd(n)}`;
const netCls = (n) => (n > 0 ? 'text-emerald-300' : n < 0 ? 'text-red-300' : 'text-zinc-300');

const Stat = ({ label, value, cls = 'text-white', testId }) => (
  <div className="rounded-lg border border-zinc-800 bg-zinc-900/70 px-3 py-2">
    <p className="text-[10px] uppercase tracking-wide text-zinc-500">{label}</p>
    <p className={`font-mono text-lg font-black ${cls}`} data-testid={testId}>{value}</p>
  </div>
);

const Row = ({ r }) => (
  <tr className="border-t border-zinc-800 text-[12px] sm:text-[13px]" data-testid={`ledger-row-${r.key}`}>
    <td className="px-1.5 py-2 font-semibold text-white whitespace-nowrap">{r.label.replace(/, \d{4}$/, '').replace(/^(\w+), /, '')}<span className="block text-[10px] font-normal text-zinc-500">{r.label.split(',')[0]}</span></td>
    <td className="px-1 py-2 text-center">{r.tickets}{r.open ? <span className="block text-[10px] text-amber-300">{r.open} open</span> : null}</td>
    <td className="px-1 py-2 text-right font-mono">{usd(r.wagered)}</td>
    <td className="px-1 py-2 text-right font-mono text-emerald-300">{usd(r.won)}</td>
    <td className="px-1 py-2 text-right font-mono text-red-300">{usd(r.lost)}</td>
    <td className={`px-1.5 py-2 text-right font-mono font-bold ${netCls(r.net)}`}>{signed(r.net)}{r.pending ? <span className="block text-[10px] font-normal text-amber-300">{usd(r.pending)} open</span> : null}</td>
  </tr>
);

// Simple by-date ledger: tickets, spent, won, lost — refreshes every 30s.
export default function LedgerPage() {
  const [d, setD] = useState(null);
  useEffect(() => {
    const get = () => axios.get(API, { params: { period: 'day' } }).then((r) => setD(r.data)).catch(() => setD((x) => x || { error: true }));
    get();
    const id = setInterval(get, 30000);
    return () => clearInterval(id);
  }, []);
  if (sessionStorage.getItem('sports_betting_authed') !== 'true') return <Navigate to="/sports-betting" replace />;
  const t = d?.totals;
  return (
    <div className="min-h-screen bg-zinc-950 text-white p-4 md:p-6" data-testid="ledger-page">
      <div className="mx-auto max-w-4xl space-y-4">
        <div className="flex items-center justify-between gap-3">
          <Link to="/sports-betting" className="flex items-center gap-1 text-sm text-zinc-400 hover:text-white" data-testid="ledger-back"><ArrowLeft className="h-4 w-4" /> My tickets</Link>
          <h1 className="flex items-center gap-2 text-lg font-bold md:text-xl"><BookOpen className="h-5 w-5 text-amber-400" />Ledger</h1>
        </div>
        {!d ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-zinc-500" /></div> : d.error ? (
          <p className="text-sm text-red-400">Could not load the ledger.</p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-5" data-testid="ledger-totals">
              <Stat label="Tickets" value={t.tickets} testId="ledger-total-tickets" />
              <Stat label="Spent" value={usd(t.wagered)} testId="ledger-total-spent" />
              <Stat label="Won" value={usd(t.won)} cls="text-emerald-300" testId="ledger-total-won" />
              <Stat label="Lost" value={usd(t.lost)} cls="text-red-300" testId="ledger-total-lost" />
              <Stat label="Up / down" value={signed(t.net)} cls={netCls(t.net)} testId="ledger-total-net" />
            </div>
            <p className="text-[11px] text-zinc-500">
              All time{d.since ? ` since ${new Date(`${d.since}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : ''} · Won = money paid back · Lost = stakes on lost tickets · open tickets count as spent and show as pending · updates live every 30s
            </p>
            <div className="overflow-x-auto rounded-xl border border-zinc-800">
              <table className="w-full" data-testid="ledger-table">
                <thead className="bg-zinc-900 text-[10px] uppercase tracking-wide text-zinc-400">
                  <tr><th className="px-1.5 py-2 text-left">Date</th><th className="px-1 py-2">Tix</th><th className="px-1 py-2 text-right">Spent</th>
                    <th className="px-1 py-2 text-right">Won</th><th className="px-1 py-2 text-right">Lost</th><th className="px-1.5 py-2 text-right">Up/down</th></tr>
                </thead>
                <tbody>{d.rows.map((r) => <Row key={r.key} r={r} />)}</tbody>
              </table>
              {!d.rows.length && <p className="py-6 text-center text-sm text-zinc-500" data-testid="ledger-empty">No tickets yet.</p>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
